'use client';

import { useCallback, useRef, useState } from 'react';

export type ScrollEdge = { atStart: boolean; atEnd: boolean };

/**
 * State + scroll actions for a horizontally scrolling card rail whose prev /
 * next buttons live inside each card (`ProcessOverviewThreeStep`'s redesign
 * carousel, and the `SideHeaderCardsRow` carousel variant).
 *
 * Cards are located via a `data-phase-card` attribute the caller stamps on
 * each card. Scrolling accounts for a leading spacer (the rail's first child)
 * so the target card lines up with the rail's start edge instead of the
 * viewport's.
 */
export function useCardCarousel() {
  const carouselRef = useRef<HTMLDivElement>(null);
  const cardIndexRef = useRef(0);
  const [scrollEdge, setScrollEdge] = useState<ScrollEdge>({
    atStart: true,
    atEnd: false,
  });

  const scrollToCard = useCallback((index: number) => {
    const el = carouselRef.current;
    if (!el) {
      return;
    }
    const cards = el.querySelectorAll<HTMLElement>('[data-phase-card]');
    const spacer = el.firstElementChild as HTMLElement | null;
    const clamped = Math.max(0, Math.min(index, cards.length - 1));
    const target = cards[clamped];
    if (!target || !spacer) {
      return;
    }
    cardIndexRef.current = clamped;
    const targetLeftInRail =
      target.getBoundingClientRect().left -
      el.getBoundingClientRect().left +
      el.scrollLeft;

    requestAnimationFrame(() => {
      el.scrollTo({
        left: targetLeftInRail - spacer.offsetWidth,
        behavior: 'smooth',
      });
    });
    setScrollEdge({
      atStart: clamped === 0,
      atEnd: clamped === cards.length - 1,
    });
  }, []);

  const scrollByCard = useCallback(
    (direction: 1 | -1) => {
      scrollToCard(cardIndexRef.current + direction);
    },
    [scrollToCard],
  );

  return { carouselRef, scrollEdge, scrollByCard, scrollToCard };
}
